/**
 * Force Password Change · SEC-TIER-1 (iter82).
 * Rendered whenever the authenticated user has team_profiles.force_password_change=true.
 * Blocks access to the rest of the app until a compliant password is set.
 *
 * The rules checklist is fetched live from /api/auth/password-policy so the
 * UI stays in sync with the backend if the policy tightens later.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldCheck, ArrowRight, Loader2, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/** Client-side mirror of the server rules (advisory only — server is the
 *  source of truth and re-runs its checks). Rules kept simple: min length,
 *  character-class coverage, uniqueness. The breached-password check
 *  happens server-side only. */
function evaluate(pwd, policy) {
  const p = pwd || "";
  const min = policy?.min_length ?? 12;
  return [
    { key: "len", label: `At least ${min} characters`, ok: p.length >= min },
    { key: "lower", label: "One lowercase letter", ok: /[a-z]/.test(p) },
    { key: "upper", label: "One uppercase letter", ok: /[A-Z]/.test(p) },
    { key: "digit", label: "One digit", ok: /\d/.test(p) },
    { key: "symbol", label: "One symbol (! @ # $ % ^ & *)", ok: /[^A-Za-z0-9]/.test(p) },
    { key: "unique", label: "At least 4 distinct characters", ok: new Set(p).size >= 4 },
  ];
}

export default function ForcePasswordChange() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [curr, setCurr] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    api.get("/auth/password-policy")
      .then(r => setPolicy(r.data))
      .catch(() => {/* non-fatal — server will still enforce on submit */});
  }, []);

  const checks = useMemo(() => evaluate(next, policy), [next, policy]);
  const allOk = checks.every(c => c.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return toast.error("Password does not meet all policy rules yet.");
    if (next !== confirmPw) return toast.error("Passwords do not match");
    if (next === curr) return toast.error("New password must differ from the current one");
    setSaving(true);
    try {
      await api.post("/auth/change-password",
                     { current_password: curr, new_password: next });
      toast.success("Password updated");
      if (refresh) await refresh();
      nav("/dashboard", { replace: true });
    } catch (err) {
      // Server-side rejection (e.g. HIBP breach hit) — surface the reason.
      toast.error(formatApiError(err) || "Unable to change password");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-hocp-alice/40 p-6"
         data-testid="force-password-page">
      <div className="w-full max-w-[460px] hocp-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-hocp-vanilla/40 text-hocp-textPrimary flex items-center justify-center">
            <ShieldCheck className="w-6 h-6" strokeWidth={1.75}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-hocp-textTertiary">Security</div>
            <div className="font-display font-bold text-[18px] text-hocp-textPrimary">Set a new password</div>
          </div>
        </div>
        <div className="text-[13px] text-hocp-textSecondary mb-5 leading-relaxed">
          For your safety, {user?.name || "you"} must set a new password before continuing.
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Current password</label>
            <Input type="password" value={curr} onChange={e => setCurr(e.target.value)}
                    className="mt-1" required data-testid="fpc-current"/>
          </div>
          <div>
            <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">New password</label>
            <Input type="password" value={next} onChange={e => setNext(e.target.value)}
                    className="mt-1" required data-testid="fpc-new"/>
          </div>
          <div>
            <label className="text-[11px] uppercase text-hocp-textTertiary font-semibold">Confirm new password</label>
            <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                    className="mt-1" required data-testid="fpc-confirm"/>
          </div>

          {/* Live policy checklist — turns green as each rule is satisfied.
              Server re-evaluates + adds a breached-password check on submit. */}
          <div className="rounded-md border border-hocp-textTertiary/20 bg-slate-50/60 p-3"
               data-testid="fpc-policy-checklist">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-hocp-textTertiary mb-2">
              Password policy
            </div>
            <ul className="space-y-1">
              {checks.map(c => (
                <li key={c.key}
                    data-testid={`fpc-check-${c.key}`}
                    className={`flex items-center gap-2 text-[12px] ${c.ok ? "text-emerald-700" : "text-slate-500"}`}>
                  {c.ok
                    ? <Check className="w-3.5 h-3.5 shrink-0"/>
                    : <X className="w-3.5 h-3.5 shrink-0"/>}
                  {c.label}
                </li>
              ))}
              {policy?.checks_breached_passwords && (
                <li className="flex items-center gap-2 text-[12px] text-slate-500 pt-1 border-t border-slate-200 mt-1">
                  <ShieldCheck className="w-3.5 h-3.5 shrink-0"/>
                  Checked against known breached-password lists (server-side)
                </li>
              )}
            </ul>
          </div>

          <Button type="submit" disabled={saving || !allOk} className="w-full" data-testid="fpc-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <KeyRound className="w-4 h-4 mr-2"/>}
            {saving ? "Updating…" : "Update Password"}
            {!saving && <ArrowRight className="w-4 h-4 ml-2"/>}
          </Button>
        </form>
      </div>
    </div>
  );
}
